import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import api from '../services/api';
import PageHeader from '../components/ui/PageHeader';
import Card from '../components/ui/Card';
import Spinner from '../components/ui/Spinner';
import Badge from '../components/ui/Badge';
import { useToast } from '../context/ToastContext';

// Heroicons stroke style — folosite în locul emoji-urilor pentru un look consistent și profesional.
// Toate primesc `currentColor` din Tailwind, deci urmează tema (light/dark).
const ICONS = {
  warning: (
    <svg fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.6} aria-hidden="true">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126ZM12 15.75h.007v.008H12v-.008Z"
      />
    </svg>
  ),
  pencilSquare: (
    <svg fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.6} aria-hidden="true">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M16.862 4.487 18.549 2.799a2.121 2.121 0 1 1 3 3L19.862 7.487m-3-3-12.121 12.121a4.5 4.5 0 0 0-1.13 1.897l-.9 3.39 3.39-.9a4.5 4.5 0 0 0 1.897-1.13L19.862 7.487m-3-3 3 3"
      />
    </svg>
  ),
  pauseCircle: (
    <svg fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.6} aria-hidden="true">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M14.25 9v6m-4.5 0V9M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z"
      />
    </svg>
  ),
  document: (
    <svg fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.6} aria-hidden="true">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M19.5 14.25v-2.625a3.375 3.375 0 0 0-3.375-3.375h-1.5A1.125 1.125 0 0 1 13.5 7.125v-1.5a3.375 3.375 0 0 0-3.375-3.375H8.25m2.25 0H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 0 0-9-9Z"
      />
    </svg>
  ),
  clock: (
    <svg fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.6} aria-hidden="true">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M12 6v6h4.5m4.5 0a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z"
      />
    </svg>
  ),
  lock: (
    <svg fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.6} aria-hidden="true">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M16.5 10.5V6.75a4.5 4.5 0 1 0-9 0v3.75m-.75 11.25h10.5a2.25 2.25 0 0 0 2.25-2.25v-6.75a2.25 2.25 0 0 0-2.25-2.25H6.75a2.25 2.25 0 0 0-2.25 2.25v6.75a2.25 2.25 0 0 0 2.25 2.25Z"
      />
    </svg>
  ),
  clipboard: (
    <svg fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.6} aria-hidden="true">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M9 12h3.75M9 15h3.75M9 18h3.75m3 .75H18a2.25 2.25 0 0 0 2.25-2.25V6.108c0-1.135-.845-2.098-1.976-2.192a48.424 48.424 0 0 0-1.123-.08m-5.801 0c-.065.21-.1.433-.1.664 0 .414.336.75.75.75h4.5a.75.75 0 0 0 .75-.75 2.25 2.25 0 0 0-.1-.664m-5.8 0A2.251 2.251 0 0 1 13.5 2.25H15c1.012 0 1.867.668 2.15 1.586m-5.8 0c-.376.023-.75.05-1.124.08C9.095 4.01 8.25 4.973 8.25 6.108V8.25m0 0H4.875c-.621 0-1.125.504-1.125 1.125v11.25c0 .621.504 1.125 1.125 1.125h9.75c.621 0 1.125-.504 1.125-1.125V9.375c0-.621-.504-1.125-1.125-1.125H8.25ZM6.75 12h.008v.008H6.75V12Zm0 3h.008v.008H6.75V15Zm0 3h.008v.008H6.75V18Z"
      />
    </svg>
  ),
  moon: (
    <svg fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.6} aria-hidden="true">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M21.752 15.002A9.72 9.72 0 0 1 18 15.75c-5.385 0-9.75-4.365-9.75-9.75 0-1.33.266-2.597.748-3.752A9.753 9.753 0 0 0 3 11.25C3 16.635 7.365 21 12.75 21a9.753 9.753 0 0 0 9.002-5.998Z"
      />
    </svg>
  ),
  checkCircle: (
    <svg fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.6} aria-hidden="true">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M9 12.75 11.25 15 15 9.75M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z"
      />
    </svg>
  ),
};

// Mapează severitatea cardului la o culoare de tint pentru iconul SVG.
const iconTint = (sev) =>
  sev === 'critical'
    ? 'text-danger-600 dark:text-danger-400'
    : sev === 'warning'
      ? 'text-amber-600 dark:text-amber-400'
      : 'text-primary-600 dark:text-primary-400';

// Pagină centralizată cu probleme operaționale ce trebuie rezolvate.
// Datele sunt scope-uite în backend pe rolul userului (SUPER_ADMIN vede tot, SSM admin vede firmele
// client + propria firmă, COMPANY_ADMIN vede firma sa, CLM vede dept-ul lui).
const IssuesDashboard = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { showToast } = useToast();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expandedCard, setExpandedCard] = useState(null); // key cardului care are toată lista deschisă

  const COLLAPSED_LIMIT = 5;

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    api
      .get('/issues')
      .then((res) => {
        if (!cancelled) setData(res.data);
      })
      .catch((err) => {
        if (!cancelled)
          showToast(err.response?.data?.error || t('issuesDashboard.loadError'), 'error');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (loading) return <Spinner label={t('issuesDashboard.loading')} />;
  if (!data) return <p className="text-sm text-slate-500">{t('issuesDashboard.loadFailed')}</p>;

  const s = data.summary;
  const fmtDate = (d) => (d ? new Date(d).toLocaleDateString('ro-RO') : '—');

  // Grupez card-urile pe severitate
  const cards = [
    {
      key: 'ip-expired',
      severity: 'critical',
      icon: ICONS.warning,
      title: t('issuesDashboard.ipExpiredTitle'),
      count: s.ipExpired,
      desc: t('issuesDashboard.ipExpiredDesc'),
      action: t('issuesDashboard.viewIpTraffic'),
      onClick: () => navigate('/dashboard-ip'),
    },
    {
      key: 'unsigned',
      severity: 'critical',
      icon: ICONS.pencilSquare,
      title: t('issuesDashboard.unsignedTitle'),
      count: s.unsignedSheets,
      desc: t('issuesDashboard.unsignedDesc'),
      action: t('issuesDashboard.viewSheetsToSign'),
      onClick: () => navigate('/sheets-to-sign'),
      details: data.lists.unsignedSheets,
      renderItem: (item) => (
        <Link
          to={`/training-details/${item.trainingId}`}
          key={item.trainingId}
          className="block py-1.5 text-xs hover:bg-slate-50 dark:hover:bg-slate-800 rounded px-2"
        >
          <span className="font-medium text-slate-900 dark:text-slate-100">
            {item.employeeName}
          </span>
          <span className="text-slate-500 dark:text-slate-400">
            {' '}
            · {item.companyName} · {item.courseTitle}
          </span>
          <span className="text-slate-400 dark:text-slate-500">
            {' '}
            · {t('issuesDashboard.itemCompleted')} {fmtDate(item.completedAt)}
          </span>
        </Link>
      ),
    },
    {
      key: 'stuck',
      severity: 'warning',
      icon: ICONS.pauseCircle,
      title: t('issuesDashboard.stuckTitle'),
      count: s.stuckTrainings,
      desc: t('issuesDashboard.stuckDesc'),
      details: data.lists.stuckTrainings,
      renderItem: (item) => (
        <Link
          to={`/training-details/${item.trainingId}`}
          key={item.trainingId}
          className="block py-1.5 text-xs hover:bg-slate-50 dark:hover:bg-slate-800 rounded px-2"
        >
          <span className="font-medium text-slate-900 dark:text-slate-100">
            {item.employeeName}
          </span>
          <span className="text-slate-500 dark:text-slate-400">
            {' '}
            · {item.companyName} · {item.courseTitle}
          </span>
          <span className="text-slate-400 dark:text-slate-500">
            {' '}
            · {t('issuesDashboard.itemStarted')} {fmtDate(item.startedAt)}
          </span>
        </Link>
      ),
    },
    {
      key: 'declarations',
      severity: 'warning',
      icon: ICONS.document,
      title: t('issuesDashboard.declarationsTitle'),
      count: s.pendingDeclarations,
      desc: t('issuesDashboard.declarationsDesc'),
      details: data.lists.pendingDeclarations,
      renderItem: (d) => (
        <div key={d.changeLogId} className="py-1.5 text-xs px-2">
          <span className="font-medium text-slate-900 dark:text-slate-100">{d.employeeName}</span>
          <span className="text-slate-500 dark:text-slate-400"> · {d.companyName}</span>
          <Badge color="purple" className="ml-1 text-[10px]">
            {d.changeType === 'NAME'
              ? t('issuesDashboard.changeTypeName')
              : t('issuesDashboard.changeTypeRoute')}
          </Badge>
          <span className="text-slate-400 dark:text-slate-500">
            {' '}
            · {t('issuesDashboard.itemSent')} {fmtDate(d.changedAt)}
          </span>
        </div>
      ),
    },
    {
      key: 'ip-soon',
      severity: 'info',
      icon: ICONS.clock,
      title: t('issuesDashboard.ipSoonTitle'),
      count: s.ipExpiringSoon,
      desc: t('issuesDashboard.ipSoonDesc'),
      action: t('issuesDashboard.viewIpTraffic'),
      onClick: () => navigate('/dashboard-ip'),
    },
    {
      key: 'gdpr',
      severity: 'info',
      icon: ICONS.lock,
      title: t('issuesDashboard.gdprTitle'),
      count: s.gdprPending,
      desc: t('issuesDashboard.gdprDesc'),
      details: data.lists.gdprPending,
      renderItem: (u) => (
        <div key={u.id} className="py-1.5 text-xs px-2">
          <span className="font-medium text-slate-900 dark:text-slate-100">
            {u.firstName} {u.lastName}
          </span>
          <span className="text-slate-500 dark:text-slate-400">
            {' '}
            · {u.email} · {u.company?.name || '—'}
          </span>
        </div>
      ),
    },
    {
      key: 'missing',
      severity: 'info',
      icon: ICONS.clipboard,
      title: t('issuesDashboard.missingTitle'),
      count: s.missingData,
      desc: t('issuesDashboard.missingDesc'),
      details: data.lists.missingDataEmployees,
      renderItem: (e) => (
        <Link
          to={`/employees/${e.employeeId}/edit`}
          key={e.employeeId}
          title={t('issuesDashboard.clickToComplete')}
          className="block py-1.5 text-xs hover:bg-primary-50 dark:hover:bg-primary-950/30 rounded px-2 group"
        >
          <span className="font-medium text-slate-900 dark:text-slate-100 group-hover:text-primary-700 dark:group-hover:text-primary-300">
            {e.name}
          </span>
          <span className="text-slate-500 dark:text-slate-400"> · {e.companyName}</span>
          <span className="text-amber-700 dark:text-amber-300">
            {' '}
            · {t('issuesDashboard.itemMissing')}: {e.missing.join(', ')}
          </span>
        </Link>
      ),
    },
    {
      key: 'inactive',
      severity: 'info',
      icon: ICONS.moon,
      title: t('issuesDashboard.inactiveTitle'),
      count: s.inactiveAccounts,
      desc: t('issuesDashboard.inactiveDesc'),
      details: data.lists.inactiveAccounts,
      renderItem: (u) => (
        <div key={u.id} className="py-1.5 text-xs px-2">
          <span className="font-medium text-slate-900 dark:text-slate-100">
            {u.firstName} {u.lastName}
          </span>
          <span className="text-slate-500 dark:text-slate-400">
            {' '}
            · {u.company?.name || '—'} · {t('issuesDashboard.itemLastLogin')}:{' '}
            {fmtDate(u.lastLoginAt) || t('issuesDashboard.never')}
          </span>
        </div>
      ),
    },
  ];

  // Sortare: critic > warning > info, count > 0 înainte
  const order = { critical: 0, warning: 1, info: 2 };
  const sorted = [...cards].sort((a, b) => {
    const aHas = a.count > 0 ? 0 : 1;
    const bHas = b.count > 0 ? 0 : 1;
    if (aHas !== bHas) return aHas - bHas;
    return order[a.severity] - order[b.severity];
  });

  const sevColor = (sev) => (sev === 'critical' ? 'red' : sev === 'warning' ? 'yellow' : 'blue');

  return (
    <div className="space-y-6">
      <PageHeader
        title={t('issuesDashboard.title')}
        subtitle={t('issuesDashboard.subtitle', {
          scope: data.scope?.all ? t('issuesDashboard.scopePlatform') : '',
          date: new Date(data.generatedAt).toLocaleString('ro-RO'),
        })}
      />

      {data.criticalCount === 0 && (
        <Card>
          <div className="text-center py-6">
            <div className="w-10 h-10 mx-auto mb-2 text-emerald-600 dark:text-emerald-400">
              {ICONS.checkCircle}
            </div>
            <p className="text-base font-semibold text-slate-900 dark:text-slate-100">
              {t('issuesDashboard.noCriticalTitle')}
            </p>
            <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">
              {t('issuesDashboard.noCriticalDesc')}
            </p>
          </div>
        </Card>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {sorted.map((c) => (
          <Card key={c.key} className={c.count === 0 ? 'opacity-60' : ''}>
            <div className="flex items-start justify-between mb-2 gap-2">
              <div className="flex items-center gap-2 min-w-0">
                <span
                  className={`w-5 h-5 flex-shrink-0 ${c.count === 0 ? 'text-slate-400 dark:text-slate-500' : iconTint(c.severity)}`}
                >
                  {c.icon}
                </span>
                <h3 className="font-semibold text-slate-900 dark:text-slate-100 text-sm truncate">
                  {c.title}
                </h3>
              </div>
              <Badge color={c.count === 0 ? 'green' : sevColor(c.severity)}>{c.count}</Badge>
            </div>
            <p className="text-xs text-slate-500 dark:text-slate-400 mb-3">{c.desc}</p>

            {c.count > 0 &&
              c.details &&
              c.details.length > 0 &&
              (() => {
                const isExpanded = expandedCard === c.key;
                const visible = isExpanded ? c.details : c.details.slice(0, COLLAPSED_LIMIT);
                const hasMore = c.details.length > COLLAPSED_LIMIT;
                const stillRemainingBeyondList = c.count - c.details.length; // backend a limitat la 50
                return (
                  <div className="border-t border-slate-100 dark:border-slate-800 pt-2">
                    <div className={isExpanded ? 'max-h-96 overflow-y-auto' : ''}>
                      {visible.map(c.renderItem)}
                    </div>
                    {hasMore && (
                      <button
                        onClick={() => setExpandedCard(isExpanded ? null : c.key)}
                        className="mt-1 text-[11px] text-primary-600 dark:text-primary-300 hover:underline px-2"
                      >
                        {isExpanded
                          ? t('issuesDashboard.collapse')
                          : t('issuesDashboard.viewAllCount', { count: c.details.length })}
                      </button>
                    )}
                    {isExpanded && stillRemainingBeyondList > 0 && (
                      <p className="text-[11px] text-slate-400 dark:text-slate-500 px-2 pt-1">
                        {t('issuesDashboard.totalItemsNote', {
                          total: c.count,
                          shown: c.details.length,
                        })}
                      </p>
                    )}
                  </div>
                );
              })()}

            {c.action && c.onClick && (
              <button
                onClick={c.onClick}
                className="mt-2 text-xs text-primary-600 dark:text-primary-300 hover:text-primary-700 hover:underline"
              >
                {c.action} →
              </button>
            )}
          </Card>
        ))}
      </div>
    </div>
  );
};

export default IssuesDashboard;
